import { FC, useLayoutEffect, useRef, useState } from 'react'
import cn from 'classnames'
import PoapImage from 'components/PoapImage'
import Status from './Status'

// Types
import { Petition } from 'backoffice/components/DropRequests/usePetitionsQuery'

// Components
import ImageModal from 'backoffice/components/DropRequests/Modals/ImageModal'
import CardButtons from './CardButtons'
import PetitionMetadata from './PetitionMetadata'
import DraftReference from './DraftReference'
import { formatDate } from 'lib/date'
import { hasEllipsis } from 'lib/dom'

// Assets
import { ReactComponent as CalendarIcon } from 'images/icons/calendar.svg'
import { ReactComponent as DescriptionIcon } from 'images/icons/description.svg'
import { ReactComponent as ReviewerIcon } from 'images/icons/secure-user.svg'
import { ReactComponent as GlobeIcon } from 'images/icons/globe.svg'

import styles from './Card.module.scss'

/* @ts-ignore TODO: remove ignore TS validation */
const DropRequestDescription: FC = ({ children }) => {
  const containertEl = useRef<HTMLDivElement>(null)
  const [overflow, setOverflow] = useState({ isCollapsed: true, hasEllipsis: false })

  const handleDescription = () => setOverflow((prev) => ({ ...prev, isCollapsed: !prev.isCollapsed }))

  useLayoutEffect(() => {
    const el = containertEl.current
    if (el) {
      setOverflow((prev) => ({ ...prev, hasEllipsis: hasEllipsis(el) }))
    }
  }, [])

  return (
    <div>
      <div ref={containertEl} className={cn(overflow.isCollapsed && styles.descriptionHidden)}>
        {children}
      </div>
      {overflow.hasEllipsis && (
        <button type="button" onClick={handleDescription} className={styles.toggleButton}>
          {overflow.isCollapsed ? 'More' : 'Less'}
        </button>
      )}
    </div>
  )
}

//TODO Refactor this component to a reusable Modal to prevent creating open/close logic everytime.
// https://poap-devs.atlassian.net/browse/FR-180
//The idea is to be able to use something like
//<Modal render={ImageModal}>
//  <img className={styles.image} src="https://via.placeholder.com/105" alt="Drop" />
//</Modal>
const DropImage: FC<{ image: string }> = ({ image }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const handleClose = () => setIsOpen(false)
  const handleClick = () => setIsOpen(true)

  return (
    <div>
      <PoapImage
        className={styles.image}
        src={image}
        loading="lazy"
        alt="Drop"
        width="104"
        height="104"
        onClick={handleClick}
      />

      <ImageModal imageUrl={image} isOpen={isOpen} onCloseClick={handleClose} />
    </div>
  )
}

type DropRequestCardProps = {
  petition: Petition
}

const DropRequestCard: FC<DropRequestCardProps> = ({ petition }) => {
  const { drop, status, amount_approved } = petition
  const [review] = petition.reviews.slice(-1)

  return (
    <div className={styles.card}>
      <DropImage image={drop.image_url} />

      <div className={styles.info}>
        <div className={styles.infoItem}>
          <CalendarIcon className={styles.icon} />
          <div aria-label="Event Duration" data-cooltipz-dir="top">
            <span className={styles.date}>{formatDate(drop.start_date)}</span>
            {' to '}
            <span className={styles.date}>{formatDate(drop.end_date)}</span>
          </div>
          <div className={styles.externalLinks}>
            <a
              href={`${process.env.REACT_APP_GALLERY_DOMAIN}/event/${drop.id}`}
              target="_blank"
              className={styles.link}
            >
              Gallery
            </a>
            <a href={`${process.env.REACT_APP_FAMILY_DOMAIN}/event/${drop.id}`} target="_blank" className={styles.link}>
              Family
            </a>
          </div>
        </div>
        {drop.drop_url && (
          <div className={styles.infoItem}>
            <GlobeIcon className={styles.icon} />
            <a href={drop.drop_url} target="_blank" className={styles.dropUrl}>
              {drop.drop_url}
            </a>
          </div>
        )}
        <div className={styles.infoItem}>
          <DescriptionIcon className={styles.icon} />
          <div aria-label="Description" data-cooltipz-dir="top">
            {/* @ts-ignore TODO: remove ignore TS validation */}
            <DropRequestDescription>{drop.description}</DropRequestDescription>
          </div>
        </div>
        <PetitionMetadata petition={petition} />
      </div>
      <hr className={styles.hr} />
      <div className={styles.footer}>
        <div className={styles.reviewInfo}>
          <ReviewerIcon width={18} height={18} className={styles.reviewerIcon} />
          <Status name={drop.name} status={status} amount={amount_approved} review={review} />
        </div>
        <CardButtons petition={petition} />
      </div>
      <DraftReference dropId={petition.drop_id} />
    </div>
  )
}

export default DropRequestCard
